import React from "react";
import { Sparkles, User, CircleDashed } from "lucide-react";

// Projektakte — the signature element of the discovery screen: the question
// bank fills itself in while the interview runs. Human answers are deep blue,
// AI-inferred answers are turquoise with confidence and rationale.

function confidenceFor(inferred, qid) {
  return inferred.find((i) => i.question_id === qid);
}

export default function Akte({ state, freshInferred = [], thinking = false }) {
  const ctx = state?.context;
  if (!ctx) return null;
  const inferred = state?.inferred || [];
  const answers = (ctx.answers || []).filter((a) => a.question_id !== "q_intake_problem");
  const open = ctx.candidates || [];
  const total = ctx.total || 0;
  const segments = [
    ...answers.map((a) => (a.source === "ai_inferred" ? "ai" : "human")),
    ...open.map(() => "open"),
  ].slice(0, Math.max(total, answers.length + open.length));

  return (
    <div className="bg-card border border-border" data-testid="dlab-akte">
      <div className="border-b border-border px-5 py-3 flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Projektakte</div>
        <div className="font-mono text-[10px] text-muted-foreground tabular-nums">
          {answers.length}/{total} geklärt
        </div>
      </div>

      {/* Segmented progress: one segment per bank question */}
      <div className="px-5 pt-4">
        <div className="flex gap-1" aria-label="Fortschritt der Fragenbank">
          {segments.map((s, i) => (
            <div
              key={i}
              className={
                "h-1.5 flex-1 rounded-full transition-colors duration-500 " +
                (s === "human" ? "bg-aw-deep-blue" : s === "ai" ? "bg-aw-turquoise" : "bg-aw-fog")
              }
            />
          ))}
        </div>
        <div className="flex gap-4 mt-2 mb-1">
          <Legend swatch="bg-aw-deep-blue" label="Ihre Antworten" />
          <Legend swatch="bg-aw-turquoise" label="Von der KI abgeleitet" />
          <Legend swatch="bg-aw-fog" label="Offen" />
        </div>
      </div>

      {/* Profile */}
      {ctx.profile && (
        <div className="px-5 py-3 border-b border-border">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Erkannter App-Typ</div>
          <span className="inline-flex items-center gap-1.5 bg-aw-deep-blue text-white text-xs font-medium px-3 py-1 rounded-full" data-testid="dlab-profile">
            {ctx.profile.name}
          </span>
        </div>
      )}

      {/* Answered */}
      <div className="divide-y divide-border">
        {answers.map((a) => {
          const isAI = a.source === "ai_inferred";
          const conf = isAI ? confidenceFor(inferred, a.question_id) : null;
          const isFresh = freshInferred.includes(a.question_id);
          return (
            <div
              key={a.question_id}
              className={
                "px-5 py-3 " +
                (isAI ? "border-l-2 border-l-aw-turquoise " : "border-l-2 border-l-aw-deep-blue ") +
                (isFresh ? "dlab-flash" : "")
              }
              data-testid={`dlab-answer-${a.question_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-xs text-muted-foreground leading-snug">{a.question}</div>
                {isAI ? (
                  <span className="shrink-0 inline-flex items-center gap-1 font-mono text-[10px] text-aw-turquoise border border-aw-turquoise/40 rounded-full px-2 py-0.5">
                    <Sparkles className="w-3 h-3" /> KI{conf?.confidence ? ` · ${Number(conf.confidence).toFixed(2)}` : ""}
                  </span>
                ) : (
                  <span className="shrink-0 inline-flex items-center gap-1 font-mono text-[10px] text-aw-deep-blue/70">
                    <User className="w-3 h-3" /> Sie
                  </span>
                )}
              </div>
              <div className="text-sm font-medium text-foreground mt-1">{a.answer}</div>
              {isAI && conf?.rationale && (
                <div className="text-[11px] text-muted-foreground mt-1 italic leading-snug">{conf.rationale}</div>
              )}
            </div>
          );
        })}

        {/* Open questions */}
        {open.map((q) => (
          <div key={q.id} className="px-5 py-2.5 border-l-2 border-l-transparent flex items-center gap-2">
            <CircleDashed className={"w-3.5 h-3.5 shrink-0 " + (thinking ? "text-aw-turquoise animate-spin" : "text-muted-foreground/50")} />
            <div className="text-xs text-muted-foreground/70 leading-snug">{q.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Legend({ swatch, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground">
      <span className={`w-2 h-2 rounded-full ${swatch}`} /> {label}
    </span>
  );
}
